import React from 'react';
import DataTable from 'react-data-table-component';
import { MapPin, User, Eye, Pen, Download, FileSpreadsheet, Printer } from 'lucide-react';
import i18n from '../../../../i18n';
import { SideBar } from '../../sidebar';
import NoData from '../../../common/NoData';
import { TableSkeleton } from '../../../common/skelton/TableSkelton';
import StatusTabs from '../../../common/StatusTab';
import HeaderBanner from '../../../common/HeaderBanner';
import { getLoggedUser } from '../../../../state/user';
import { getDeliverySiteList, getOverallCollectionSummary } from './services/DeliverySiteService';
import { Link } from 'react-router-dom';
import { tableStyles } from '../../../../styles/components/js/tableStyle';
import FilterSection from '../../../common/Filter';
import DateRangeSelector from '../../../common/DateRange';
import { toast } from 'react-toastify';
import {
  getAuctionList,
  getGroupsSelectors,
  getOrganizationSelectors,
} from '../LoadingManagement/services/LoadingManagementService';

export class DeliverySiteList extends React.Component {
  constructor() {
    super();
    this.state = {
      // Filter state
      search: {
        group: '',
        auction: '',
        status: '',
        bidder: null,
      },
      siteVisitStatus: 'all',

      // Client/Group/Auction selection
      client: '',
      clients: [],
      groupName: '',
      groups: [],
      auction: '',
      auctions: [],

      // Loading states
      loadingClients: false,
      loadingGroups: false,
      loadingAuctions: false,

      // Data
      siteVisits: [],
      selectedUser: {},
      showUserPopUp: false,
      statusData: [
        { key: 'all', label: 'All', count: 0 },
        { key: 'P', label: 'Scheduled', count: 0 },
        { key: 'T', label: 'In Transit', count: 0 },
        { key: 'C', label: 'Completed', count: 0 },
        { key: 'D', label: 'Delayed', count: 0 },
      ],

      // Pagination
      is_loading: false,
      limit: 15,
      page: 1,
      total: 0,

      // UI state
      rejectNote: null,
      rejectNoteError: null,
      hideSidebar: false,
      showModal: false,
      clearDate: false, // Added for DateRangeSelector
    };

    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleRowsPerPageChange = this.handleRowsPerPageChange.bind(this);
    this.handleDateRangeChange = this.handleDateRangeChange.bind(this);
    this.handleSearchSubmit = this.handleSearchSubmit.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(window.location.search);
    const search = {
      group: params.get('group') || '',
      auction: params.get('auction') || '',
      status: params.get('status') || '',
      bidder: params.get('bidder') || null,
    };
    const siteVisitStatus = params.get('siteVisitStatus') || 'all';
    const page = parseInt(params.get('page')) || 1;
    const limit = parseInt(params.get('limit')) || 15;

    this.setState({ search, siteVisitStatus, page, limit }, () => {
      this.fetchDeliverySiteList();
    });
    this.fetchAllOrganization();
  }

  updateUrlQuery(q) {
    const queryString = new URLSearchParams(
      Object.fromEntries(
        Object.entries(q).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      )
    ).toString();
    window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
  }

  async fetchDeliverySiteList() {
    this.setState({ is_loading: true });
    let { limit, page, siteVisitStatus, search } = this.state;

    try {
      const filters = {
        page: page,
        per_page: limit,
      };

      if (siteVisitStatus && siteVisitStatus !== 'all') {
        filters.status = siteVisitStatus;
      }

      if (search.group) {
        filters.group_id = search.group;
      }

      if (search.auction) {
        filters.auction_id = search.auction;
      }

      if (search.bidder) {
        filters.buyer_id = search.bidder;
      }

      const response = await getDeliverySiteList(filters);

      if (response.success) {
        let siteVisits = response.data || [];
        let meta = response.meta || {};

        const statusCounts = meta.status_counts || {
          total: 0,
          scheduled: 0,
          in_transit: 0,
          completed: 0,
          delayed: 0,
        };

        this.setState({
          siteVisits: siteVisits,
          total: meta.total || 0,
          statusData: [
            { key: 'all', label: 'All', count: statusCounts.total || 0 },
            { key: 'P', label: 'Scheduled', count: statusCounts.scheduled || 0 },
            { key: 'T', label: 'In Transit', count: statusCounts.in_transit || 0 },
            { key: 'C', label: 'Completed', count: statusCounts.completed || 0 },
            { key: 'D', label: 'Delayed', count: statusCounts.delayed || 0 },
          ],
          is_loading: false,
        });

        // Update URL with current filters
        this.updateUrlQuery({
          ...search,
          siteVisitStatus,
          page,
          limit,
        });
      } else {
        console.error('Failed to fetch delivery schedules:', response.message);
        this.setState({
          siteVisits: [],
          is_loading: false,
        });
        toast.error(response.message || 'Failed to fetch delivery schedules');
      }
    } catch (error) {
      console.error('Error fetching delivery schedules:', error);
      this.setState({
        siteVisits: [],
        is_loading: false,
      });
      toast.error('Error fetching delivery schedules');
    }
  }

  handleTabChange = (key) => {
    this.setState({ siteVisitStatus: key, page: 1 }, () => {
      this.fetchDeliverySiteList();
    });
  };

  handlePageChange(page) {
    this.setState({ page: page }, () => {
      this.fetchDeliverySiteList();
    });
  }

  handleRowsPerPageChange(newLimit, page) {
    this.setState({ limit: newLimit, page: 1 }, () => {
      this.fetchDeliverySiteList();
    });
  }

  handleRowClick = (row) => {
    if (this.props.history && row.id) {
      this.props.history.push(`/delivery-site/edit/${row.id}`);
    }
  };

  handleEdit = (row, e) => {
    e?.stopPropagation();
    if (this.props.history && row.id) {
      this.props.history.push(`/delivery-site/edit/${row.id}`);
    }
  };

  handleSummary = async (row, e) => {
    e?.stopPropagation();
    try {
      // Download as Excel
      const response = await getOverallCollectionSummary(row.id, 'excel');

      // Create blob from response
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate file name
      const fileName = `Collection_Summary_${row.lot_ref}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.download = fileName;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading summary:', error);
      toast.error('Failed to download Excel file. Please try again.');
    }
  };

  fetchAllOrganization = async () => {
    this.setState({ loadingClients: true });

    try {
      const response = await getOrganizationSelectors();
      if (response.success && response.data) {
        const clients = response.data.map((client) => ({
          id: client.id,
          value: client.id,
          label: client.organization_name,
          organization_name: client.organization_name,
        }));

        this.setState({
          clients,
          loadingClients: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch organization');
        this.setState({ loadingClients: false });
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
      toast.error('Error loading organization list');
      this.setState({ loadingClients: false });
    }
  };

  fetchGroupsList = async (organizationId) => {
    this.setState({ loadingGroups: true });

    try {
      const filters = {
        organization_id: organizationId,
      };

      const response = await getGroupsSelectors(filters);

      if (response.success && response.data) {
        const groups = response.data.map((group) => ({
          id: group.id,
          value: group.id,
          label: group.group_name,
          name: group.group_name,
          organization_id: group.organization_id,
        }));

        this.setState({
          groups,
          loadingGroups: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch groups');
        this.setState({ loadingGroups: false, groups: [] });
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Error loading groups list');
      this.setState({
        loadingGroups: false,
        groups: [],
        groupName: '',
      });
    }
  };

  fetchAuctionList = async (groupId) => {
    this.setState({ loadingAuctions: true });

    try {
      const filters = { groupId: groupId }; // Changed from groupId to group_id based on API expectation
      const response = await getAuctionList(filters);

      if (response.success && response.data) {
        const auctions = response.data.map((auction) => ({
          id: auction.id,
          value: auction.id,
          label: auction.title,
          name: auction.title,
          winners: auction.winners || [],
        }));

        this.setState({
          auctions,
          loadingAuctions: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch auction');
        this.setState({ loadingAuctions: false, auctions: [] });
      }
    } catch (error) {
      console.error('Error fetching auction:', error);
      toast.error('Error loading auction list');
      this.setState({
        loadingAuctions: false,
        auctions: [],
      });
    }
  };

  handleClientChange = (e) => {
    const clientId = e.target.value;
    const selectedClient = this.state.clients.find((c) => c.id == clientId);

    this.setState({
      client: clientId,
      groupName: '',
      groups: [],
      auction: '',
      auctions: [],
    });

    if (clientId) {
      this.fetchGroupsList(clientId);
      if (selectedClient) {
        toast.success(`Client "${selectedClient.organization_name}" selected. Loading groups...`);
      }
    }
  };

  handleGroupChange = (e) => {
    const groupId = e.target.value;
    const selectedGroup = this.state.groups.find((g) => g.id == groupId);

    this.setState({
      groupName: groupId,
      auction: '',
      auctions: [],
    });

    // Update search state
    this.setState((prevState) => ({
      search: {
        ...prevState.search,
        group: groupId,
        auction: '', // Clear auction in search when group changes
      },
    }));

    // Fetch auctions for the selected group
    if (groupId) {
      this.fetchAuctionList(groupId);
      if (selectedGroup) {
        toast.success(`Group "${selectedGroup.label}" selected. Loading auctions...`);
      }
    }
  };

  handleAuctionChange = (auctionId) => {
    const selectedAuction = this.state.auctions.find((a) => a.id == auctionId);

    this.setState({ auction: auctionId });

    // Update search state
    this.setState(
      (prevState) => ({
        search: {
          ...prevState.search,
          auction: auctionId,
        },
      }),
      () => {
        // Trigger data fetch with the selected auction
        this.fetchDeliverySiteList();
      }
    );

    if (selectedAuction) {
      toast.success(`Auction "${selectedAuction.label}" selected.`);
    }
  };

  handleSearchSubmit = () => {
    // Apply the current filters and fetch data
    this.setState({ page: 1 }, () => {
      this.fetchDeliverySiteList();
    });
  };

  handleDateRangeChange = (range) => {
    // Handle date range change
    this.setState(
      (prevState) => ({
        search: {
          ...prevState.search,
          date_from: range.startDate,
          date_to: range.endDate,
        },
        page: 1,
      }),
      () => {
        this.fetchDeliverySiteList();
      }
    );
  };

  clearFilters = () => {
    this.setState(
      {
        client: '',
        groupName: '',
        auction: '',
        groups: [],
        auctions: [],
        search: {
          group: '',
          auction: '',
          status: '',
          bidder: null,
        },
        siteVisitStatus: 'all',
        page: 1,
        clearDate: !this.state.clearDate, // Toggle to reset DateRangeSelector
      },
      () => {
        this.fetchDeliverySiteList();
      }
    );
  };

  // Add a method to download as Excel
  downloadSummaryAsExcel = (summaryData, row) => {
    // Create CSV content
    const header = `
Auction: ${summaryData.header.auction}
Sale Lot: ${summaryData.header.sale_lot}
Buyer: ${summaryData.header.buyer}
Collection Status: ${summaryData.header.collection_status_label}
Status Date: ${summaryData.header.status_date}

`;

    const tableHeader =
      'Sl No,LOT REF,BUYER,Qty (Tons),Start Date,Planned Finish Date,Actual Finish Date & Days,Days Remaining,Area,Yard location,PLAN,Total of Prev. Day,For the Day,Cum.Total,Progress %,Balance,Start Date,Finish Date,Status,Date,Remaining Items,Status,Date,Technical Release (YES/NO),Financial Release (YES/NO),Remarks\n';

    const tableRow = [
      summaryData.delivery_site_info.sl_no,
      summaryData.delivery_site_info.lot_ref,
      summaryData.delivery_site_info.buyer,
      summaryData.delivery_site_info.quantity_tons,
      summaryData.delivery_site_info.start_date,
      summaryData.delivery_site_info.planned_finish_date,
      summaryData.delivery_site_info.actual_finish_date,
      summaryData.delivery_site_info.days_remaining || '',
      summaryData.delivery_site_info.area,
      summaryData.delivery_site_info.yard_location,
      summaryData.delivery_site_info.plan,
      summaryData.progressive_collection.total_of_previous_day,
      summaryData.progressive_collection.for_the_day,
      summaryData.progressive_collection.cumulative_total,
      summaryData.progressive_collection.progress_percentage,
      summaryData.progressive_collection.balance,
      summaryData.tentative_dates.start_date,
      summaryData.tentative_dates.finish_date,
      summaryData.collection_status,
      '', // Date
      '', // Remaining Items
      summaryData.housekeeping_status,
      '', // Date
      summaryData.technical_release,
      summaryData.financial_release,
      summaryData.remarks,
    ].join(',');

    const csvContent = header + tableHeader + tableRow;

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `Collection_Summary_Site_${row.id}_${row.lot_ref}_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  getStatusBadge = (status) => {
    const statusMap = {
      P: { label: 'Scheduled', color: '#3b82f6', bgColor: '#dbeafe' },
      T: { label: 'In Transit', color: '#8b5cf6', bgColor: '#f3e8ff' },
      C: { label: 'Completed', color: '#10b981', bgColor: '#d1fae5' },
      D: { label: 'Delayed', color: '#f59e0b', bgColor: '#fef3c7' },
    };

    const statusInfo = statusMap[status] || { label: status, color: '#6b7280', bgColor: '#f3f4f6' };

    return (
      <span
        style={{
          backgroundColor: statusInfo.bgColor,
          color: statusInfo.color,
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '500',
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <span
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: statusInfo.color,
          }}
        />
        {statusInfo.label}
      </span>
    );
  };

  getCompletionStatusBadge = (progress) => {
    let statusInfo;

    if (progress >= 100) {
      statusInfo = { label: 'Completed', color: '#10b981', bgColor: '#d1fae5' };
    } else if (progress >= 75) {
      statusInfo = { label: 'Almost Complete', color: '#8b5cf6', bgColor: '#f3e8ff' };
    } else if (progress >= 50) {
      statusInfo = { label: 'Halfway', color: '#3b82f6', bgColor: '#dbeafe' };
    } else if (progress > 0) {
      statusInfo = { label: 'In Progress', color: '#f59e0b', bgColor: '#fef3c7' };
    } else {
      statusInfo = { label: 'Not Started', color: '#6b7280', bgColor: '#f3f4f6' };
    }

    return (
      <span
        style={{
          backgroundColor: statusInfo.bgColor,
          color: statusInfo.color,
          padding: '4px 10px',
          borderRadius: '20px',
          fontSize: '11px',
          fontWeight: '500',
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        {statusInfo.label}
      </span>
    );
  };

  formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  formatWeight = (weight) => {
    return parseFloat(weight || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  getColumns() {
    return [
      {
        name: '#',
        selector: (row, index) => index + 1,
        width: '60px',
        center: true,
      },
      {
        name: '',
        center: true,
        cell: (row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <Link
              className="btn-new button1-primary"
              to={`/delivery-site/edit/${row.id}`}
              onClick={(e) => this.handleEdit(row, e)}
            >
              <Eye size={14} />
            </Link>
          </div>
        ),
      },
      {
        name: 'Project Name',
        selector: (row) => row.delivery_schedule?.project_name || '-',
        sortable: true,
        width: '180px',
        cell: (row) => (
          <Link to={`/delivery-site/edit/${row.id}`} onClick={(e) => this.handleEdit(row, e)}>
            <div style={{ fontWeight: '600', color: '#1e40af' }}>
              {row.delivery_schedule?.project_name || '-'}
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
              Lot: {row.lot_ref || '-'}
            </div>
          </Link>
        ),
      },
      {
        name: 'Supervisor',
        selector: (row) => row.supervisor?.name || row.supervisor_name || '-',
        sortable: true,
        width: '130px',
        cell: (row) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <User size={14} color="#6b7280" />
            <span style={{ fontSize: '13px' }}>
              {row.supervisor?.name || row.supervisor_name || '-'}
            </span>
          </div>
        ),
      },
      // {
      //     name: 'Area',
      //     selector: row => row.area_place?.place_name || '-',
      //     sortable: true,
      //     width: '120px',
      //     cell: row => (
      //         <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      //             <MapPin size={14} color="#6b7280" />
      //             <span style={{ fontSize: '13px' }}>
      //                 {row.area_place?.place_name || '-'}
      //             </span>
      //         </div>
      //     ),
      // },
      {
        name: 'Weight Progress',
        selector: (row) => row.progress || 0,
        sortable: true,
        width: '200px',
        cell: (row) => {
          const progress = parseFloat(row.progress) || 0;
          const loadedWeight = parseFloat(row.loaded_weight) || 0;
          const expectedWeight = parseFloat(row.expected_weight) || 0;
          const remainingWeight = parseFloat(row.remaining_weight) || 0;

          return (
            <div className="weight-progress-container">
              <div className="progress-header" style={{ marginBottom: '6px' }}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                    {progress.toFixed(1)}%
                  </span>
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>
                    {this.formatWeight(loadedWeight)} / {this.formatWeight(expectedWeight)} KG
                  </span>
                </div>
              </div>

              <div className="progress-bar-container" style={{ marginBottom: '6px' }}>
                <div
                  style={{
                    width: '100%',
                    height: '6px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '3px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${progress}%`,
                      height: '100%',
                      backgroundColor:
                        progress >= 100
                          ? '#10b981'
                          : progress >= 75
                            ? '#8b5cf6'
                            : progress >= 50
                              ? '#3b82f6'
                              : '#f59e0b',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>

              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                {this.getCompletionStatusBadge(progress)}
                <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>
                  {remainingWeight > 0
                    ? `${this.formatWeight(remainingWeight)} KG left`
                    : 'Complete'}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        name: 'Load From/To',
        width: '120px',
        cell: (row) => (
          <div style={{ fontSize: '12px' }}>
            <div style={{ marginBottom: '4px' }}>
              <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>From:</div>
              <div style={{ fontWeight: '500' }}>{row.load_from || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>To:</div>
              <div style={{ fontWeight: '500' }}>{row.load_to || '-'}</div>
            </div>
          </div>
        ),
      },
      {
        name: 'Status',
        selector: (row) => row.status,
        sortable: true,
        width: '120px',
        cell: (row) => this.getStatusBadge(row.status),
      },
      {
        name: 'Dates',
        width: '120px',
        cell: (row) => (
          <div style={{ fontSize: '12px' }}>
            <div style={{ marginBottom: '4px' }}>
              <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Start:</div>
              <div style={{ fontWeight: '500' }}>
                {this.formatDate(row.expecting_start_date) || '-'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>End:</div>
              <div style={{ fontWeight: '500' }}>
                {this.formatDate(row.expecting_end_date) || '-'}
              </div>
            </div>
          </div>
        ),
      },
      {
        name: 'Actions',
        width: '120px',
        center: true,
        cell: (row) => (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <Link
              className="button1 button1-primary"
              to={`/delivery-site/edit/${row.id}`}
              onClick={(e) => this.handleEdit(row, e)}
            >
              <Pen size={14} />
            </Link>
          </div>
        ),
      },
    ];
  }

  renderTable() {
    const { siteVisits = [], limit = 15, page = 1, total = 0 } = this.state;

    const customTableStyles = {
      ...tableStyles,
      rows: {
        ...tableStyles.rows,
        style: {
          ...tableStyles.rows?.style,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#f8f9fa',
          },
        },
      },
    };

    return (
      <div className="delivery-schedule-table">
        <DataTable
          key={`${page}-${limit}`}
          title=""
          columns={this.getColumns()}
          data={siteVisits}
          customStyles={customTableStyles}
          highlightOnHover
          pagination
          paginationServer
          paginationTotalRows={total}
          paginationPerPage={limit}
          paginationDefaultPage={page}
          paginationRowsPerPageOptions={[15, 25, 50, 100]}
          paginationComponentOptions={{
            rowsPerPageText: i18n.t('rows_per_page'),
            rangeSeparatorText: i18n.t('out'),
          }}
          onChangeRowsPerPage={this.handleRowsPerPageChange}
          onChangePage={this.handlePageChange}
          onRowClicked={this.handleRowClick}
        />
      </div>
    );
  }

  render() {
    const user = getLoggedUser();
    const {
      is_loading,
      siteVisits,
      siteVisitStatus,
      statusData,
      client,
      clients,
      loadingClients,
      groupName,
      groups,
      loadingGroups,
      auctions,
      auction,
      loadingAuctions,
      hideSidebar,
      clearDate,
    } = this.state;

    return (
      <React.Fragment>
        <HeaderBanner
          title={i18n.t('delivery_site_list')}
          subtitle={i18n.t('manage_delivery_site_list')}
        />
        <section className="mt-5">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>
              <div
                className={
                  hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => {
                          this.setState({
                            hideSidebar: !hideSidebar,
                          });
                        }}
                      >
                        {hideSidebar ? (
                          <i className="icon-size-actual"></i>
                        ) : (
                          <i className="icon-size-fullscreen"></i>
                        )}
                      </span>
                    </div>
                  </div>

                  <FilterSection
                    loading={is_loading}
                    onSearch={this.handleSearchSubmit}
                    onClear={this.clearFilters}
                  >
                    <div className="select-wrapper floating-label-wrapper">
                      <select
                        value={client}
                        onChange={this.handleClientChange}
                        disabled={loadingClients}
                        className="filter-base floating-input"
                      >
                        <option value="">Select Client</option>
                        {loadingClients ? (
                          <option value="" disabled>
                            Loading Clients...
                          </option>
                        ) : (
                          clients.map((client) => (
                            <option key={client.id} value={client.id}>
                              {client.label}
                            </option>
                          ))
                        )}
                      </select>
                      <label className="floating-label">{i18n.t('organization')}</label>
                    </div>

                    <div className="select-wrapper floating-label-wrapper">
                      <select
                        value={groupName}
                        onChange={this.handleGroupChange}
                        className="filter-base floating-input"
                        disabled={loadingGroups || !client}
                      >
                        <option value="">Select Group</option>
                        {!client ? (
                          <option value="" disabled>
                            Select a client first
                          </option>
                        ) : loadingGroups ? (
                          <option value="" disabled>
                            Loading Groups...
                          </option>
                        ) : groups.length === 0 ? (
                          <option value="" disabled>
                            No groups found for this client
                          </option>
                        ) : (
                          groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.label}
                            </option>
                          ))
                        )}
                      </select>
                      <label className="floating-label">{i18n.t('project_name')}</label>
                    </div>

                    <div className="select-wrapper floating-label-wrapper">
                      <select
                        id="auction-select"
                        value={auction}
                        className="filter-base floating-input"
                        onChange={(e) => this.handleAuctionChange(e.target.value)}
                        disabled={loadingAuctions || !groupName}
                      >
                        <option value="">Select Auction</option>
                        {loadingAuctions ? (
                          <option value="" disabled>
                            Loading auctions...
                          </option>
                        ) : !groupName ? (
                          <option value="" disabled>
                            Select a group first
                          </option>
                        ) : auctions.length === 0 ? (
                          <option value="" disabled>
                            No auctions found for this group
                          </option>
                        ) : (
                          auctions.map((auction) => (
                            <option key={auction.id} value={auction.id}>
                              {auction.label}
                            </option>
                          ))
                        )}
                      </select>
                      <label className="floating-label">{i18n.t('auction_name')}</label>
                    </div>

                    <DateRangeSelector
                      onChange={this.handleDateRangeChange}
                      clearTrigger={clearDate}
                    />
                  </FilterSection>

                  <div className="card-body">
                    <StatusTabs
                      tabs={statusData}
                      onTabChange={this.handleTabChange}
                      selected={siteVisitStatus}
                    />

                    <div className="Data-Listing">
                      {is_loading ? (
                        <TableSkeleton columns={8} rows={10} />
                      ) : siteVisits.length ? (
                        this.renderTable()
                      ) : (
                        <NoData
                          message="No delivery schedules available"
                          actionText="Refresh"
                          onAction={this.fetchDeliverySiteList.bind(this)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}
